import { CardRow } from '@components/elements/card/card';
import HomeSection from '@components/home/home-section/home-section';
import PageTitleBar from '@components/layout/page-title-bar/page-title-bar';
import RestaurantCard from '@components/restaurants/restaurant-card/restaurant-card';
import {
  v2_asBranchFallback,
  v2_fetchBranch,
  v2_useBranch,
  v2_useBranches,
} from '@crocodille/common';
import { assert, getDayOfWeek } from '@crocodille/util';
import { AppPage } from '@framework/page';
import PageMeta from '@framework/page-meta';
import { getAboutLayout } from '@layouts/about-layout/about-layout';
import BranchDetail, {
  BranchDetailBanner,
} from '@sections/branch/branch-detail';
import { getApp } from '@utils/app';
import { getServerSideTranslationProps } from '@utils/i18n';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useTranslation } from 'react-i18next';

export interface RestaurantDetailPageProps {
  readonly code: string;
  readonly fallback: Record<string, unknown>;
}

const RestaurantDetailPage: AppPage<RestaurantDetailPageProps> = ({ code }) => {
  const { t } = useTranslation();
  const { data: branch } = v2_useBranch(code);
  const { data: branches } = v2_useBranches();

  if (!branch) {
    return null;
  }

  const today = getDayOfWeek(new Date());

  return (
    <>
      <PageMeta pageTitle={branch.name} description={''} />
      <BranchDetailBanner imageSrc={branch.imagePath ?? null} />
      <PageTitleBar title={branch.name} />
      <BranchDetail branch={branch} />
      <HomeSection title={t('home.section.branches')}>
        <CardRow>
          {branches
            ?.filter?.((b) => b.code !== code)
            ?.map((branch) => (
              <RestaurantCard
                key={branch.code}
                name={branch.name}
                imageSrc={branch.imagePath}
                address={branch.address}
                openFrom={branch.openingHours[today]?.openFrom}
                openTo={branch.openingHours[today]?.openTo}
                deliveryFrom={branch.openingHours[today]?.deliveryFrom}
                deliveryTo={branch.openingHours[today]?.deliveryTo}
                small={true}
                code={branch.code}
              />
            ))}
        </CardRow>
      </HomeSection>
    </>
  );
};

RestaurantDetailPage.getLayout = (page, pageProps) => {
  return getAboutLayout(page, pageProps.fallback);
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const app = getApp(ctx);

  const branchCode = ctx.params?.code;

  assert(typeof branchCode === 'string');

  const [translationProps, branch] = await Promise.all([
    getServerSideTranslationProps(app, ['common', 'restaurants']),
    v2_fetchBranch(app, branchCode),
  ]);

  if (!branch) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      code: branchCode,
      ...translationProps,
      fallback: { ...v2_asBranchFallback(app, branch) },
    },
  };
};

export default RestaurantDetailPage;
